import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { adminApi } from '@/services/api';
import { useAdminStore } from '@/store/admin-store';
import { Button, Input } from '@nepal-ecom/ui';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(6, 'At least 6 characters'),
});

type FormData = z.infer<typeof schema>;

export function Login() {
  const { setTokens, setAdmin, isAuthenticated } = useAdminStore();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  if (isAuthenticated) return <Navigate to="/" replace />;

  const onSubmit = async (data: FormData) => {
    try {
      const res = await adminApi.login(data.email, data.password);
      const { accessToken, refreshToken, user } = res.data.data;
      // Admins, super-admins, and staff (Catalog/Order Managers, etc.) may all
      // sign in. Staff are scoped to their permissions once inside.
      if (user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'staff') {
        setError('root', { message: 'This account does not have admin access.' });
        return;
      }
      if (user.role === 'staff' && (!user.permissions || user.permissions.length === 0)) {
        setError('root', { message: 'Your staff account has no permissions yet. Ask an admin to assign a role.' });
        return;
      }
      setTokens(accessToken, refreshToken);
      setAdmin({
        id: user.id,
        name: user.fullName || user.email,
        email: user.email,
        role: user.role,
        permissions: user.permissions ?? [],
        avatar: user.avatarUrl,
      });
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) setError('root', { message: 'Invalid email or password.' });
      else setError('root', { message: 'Could not reach the server. Try again in a moment.' });
    }
  };

  return (
    <div className="min-h-screen sidebar-surface flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-32 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-cobalt-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-xl mx-auto mb-4 shadow-lg shadow-primary-900/40">श</div>
          <h1 className="text-xl font-bold text-white tracking-tight">Admin Console</h1>
          <p className="text-slate-400 text-sm mt-1">Shishyastra Platform</p>
        </div>

        <div className="bg-slate-900/60 backdrop-blur-sm rounded-2xl border border-slate-800 p-7 shadow-pop">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-300">Email</label>
              <Input
                {...register('email')}
                type="email"
                placeholder="admin@nepalmart.com"
                className="bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-500 focus-visible:border-primary-500"
              />
              {errors.email && (
                <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-300">Password</label>
              <Input
                {...register('password')}
                type="password"
                placeholder="••••••••"
                className="bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-500 focus-visible:border-primary-500"
              />
              {errors.password && (
                <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>
              )}
            </div>

            {errors.root && (
              <div className="bg-red-950/40 border border-red-900/60 rounded-lg p-3 text-sm text-red-300 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                {errors.root.message}
              </div>
            )}

            <Button type="submit" disabled={isSubmitting} size="lg" className="w-full">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>
        </div>

        <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5" />
          Internal access only · audited session
        </div>
      </div>
    </div>
  );
}
